import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import router from 'next/router';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { capitalize } from 'lodash';
import { ServerSideErrorModal, LoadingView } from '@web/components';
import {
  freshModalsContent,
  dryModalsContent,
} from '@web/testing/modalConstants';
import { ApiResult, wrapErrorWithResult } from '../../api';
import { OnboardingIntro } from '../OnboardingIntro';
import { QuizAbandonerMenu } from '../QuizAbandonerMenu';
import { PupNameForm } from '../PupNameForm';
import { PupGenderForm } from '../PupGenderForm';
import { PupBreedForm } from '../PupBreedForm';
import { PupStatusForm } from '../PupStatusForm';
import { CaloriesForm } from '../CaloriesForm';
import { ActivityForm } from '../ActivityForm';
import planHeaderPhoto from '../../images/product-photos/pack-shots/pack-on-board-with-shadow.png';
import { QuizHeader } from '../QuizHeader';
import { CarouselMealPlanForm } from '../CarouselMealPlanForm';
import { OptimizedMealPlanForm } from '../OptimizedMealPlanForm';
import { AllergyForm } from '../AllergyForm';
import { useAllergies } from '../../allergies';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { ExtrasSelectionForm } from '../ExtrasSelectionForm';
import { GiftWithPurchaseForm } from '../GiftWithPurchaseForm';
import {
  optimizedRecipes,
  carouselFormRecipes,
  dryRecipes,
} from '../constants';
import { BC_TO_SHOPIFY_SKU_MAP } from '../../constants';
import { OnboardingContext } from './context';
import {
  buildCheckoutRedirectUrl,
  buildProductTypeInterpolationSubstitutes,
  createExtrasSkipHandler,
  createExtrasSubmitHandler,
  interpolations,
  mergeAddonModals,
  mergeAddonSections,
  partitionAddonsByAvailability,
  resolveAbandonerMenuConfig,
  resolveOnboardingPets,
  resolveRecipeConfig,
  resolveQuizProgress,
  resolveQuizProgressStep,
} from './helpers';
import { readFurthestStep, bucketFromFurthestStep } from './furthestStep';
import {
  onCartSubmission,
  getUnavailableRecipes,
  getSelectedAllergies,
  getWeightCallout,
  getUnavailableMealPrepTypes,
  normalize,
} from './functions';
import { STATE, ACTIONS, levelToLabel } from './constants';
import { showPupBreedStep } from './guard';
import { Main, Wrapper } from './styles';

const QuizContainer = ({
  quizStep,
  children,
  showPromoBar = false,
  progress = true,
  overrideText = '',
  centerLogo = false,
}) => {
  const { flags, showModal, setShowModal } = useContext(OnboardingContext);
  const { stepNumber, totalSteps } = quizStep
    ? resolveQuizProgressStep(flags, quizStep)
    : { stepNumber: 1, totalSteps: 1 };

  return (
    <Wrapper>
      <QuizHeader
        showPromoBar={showPromoBar}
        progress={progress}
        overrideText={overrideText}
        currentStep={stepNumber}
        totalSteps={totalSteps}
        centerLogo={centerLogo}
      />
      <Main>{children}</Main>
      <ServerSideErrorModal
        shown={showModal}
        headerText="Hmm, something's not right."
        errorMessage="We weren't able to complete your request, please refresh the page and try again."
        onCloseModal={() => setShowModal(false)}
      />
    </Wrapper>
  );
};

QuizContainer.propTypes = {
  quizStep: PropTypes.oneOf([
    'pupName',
    'pupGender',
    'pupBreed',
    'pupStatus',
    'weight',
    'activity',
    'allergies',
  ]),
  children: PropTypes.object.isRequired,
  showPromoBar: PropTypes.bool,
  progress: PropTypes.bool,
  overrideText: PropTypes.string,
  centerLogo: PropTypes.string,
};

const IntroPage = props => {
  const offer = useShopifyOfferContext();
  const { send, isFetchingOfferFlags } = useContext(OnboardingContext);
  const goBack = () => router.push('/');

  if (isFetchingOfferFlags) return null;

  return (
    <OnboardingIntro
      {...props}
      goBack={goBack}
      onContinue={() => send(ACTIONS.toPupName())}
      popUpText={
        props.popUpText &&
        interpolate(props.popUpText, { offer: offer.formattedAmount })
      }
    />
  );
};

const AbandonerMenu = props => {
  const {
    pet,
    cart,
    cachedCart,
    send,
    reporter,
    setShowModal,
    isRouteChanging,
  } = useContext(OnboardingContext);
  const offer = useShopifyOfferContext();

  const { quizContent, planContent, checkoutContent, ...pageProps } = props;

  const { resolvedPets, resolvedPet } = resolveOnboardingPets({
    pet,
    cart,
    cachedCart,
  });

  const furthestStep = useMemo(() => readFurthestStep(), []);
  const bucket = useMemo(
    () => bucketFromFurthestStep(furthestStep),
    [furthestStep],
  );

  const safeSend = useCallback(
    action => {
      try {
        send(action);
      } catch (error) {
        reporter?.error?.(error);
        setShowModal?.(true);
      }
    },
    [send, reporter, setShowModal],
  );

  const menuConfig = useMemo(
    () =>
      resolveAbandonerMenuConfig({
        bucket,
        resolvedPet,
        resolvedPets,
        quizContent,
        planContent,
        checkoutContent,
        pageProps,
      }),
    [
      bucket,
      resolvedPet,
      resolvedPets,
      quizContent,
      planContent,
      checkoutContent,
      pageProps,
    ],
  );

  useEffect(() => {
    if (menuConfig.shouldFallBackToIntro) {
      try {
        send(ACTIONS.toIntro());
      } catch (error) {
        reporter?.error?.(error);
      }
    }
  }, [menuConfig.shouldFallBackToIntro, send, reporter]);

  if (menuConfig.shouldFallBackToIntro) return null;

  const secondaryCtaButtons = menuConfig.secondaryActions.map(
    ({ action, secondaryCtaTextTemplate, pet: actionPet }) => ({
      secondaryCtaText: interpolate(
        secondaryCtaTextTemplate,
        interpolations(actionPet),
      ),
      onSecondaryCtaClick: () => safeSend(action),
    }),
  );

  const cmsProps = interpolate(
    { ...pageProps, ...menuConfig.content },
    { ...interpolations(resolvedPet), offer: offer.formattedAmount },
  );

  return (
    <QuizAbandonerMenu
      {...cmsProps}
      multipetBannerText={menuConfig.multipetBannerText}
      onPrimaryCtaClick={() => safeSend(menuConfig.primaryAction)}
      secondaryCtaButtons={secondaryCtaButtons}
      isCtaLoading={Boolean(isRouteChanging)}
      petCount={resolvedPets?.length}
      furthestStep={furthestStep}
      bucket={bucket}
    />
  );
};

const PupName = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack, flags } =
    useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const defaultValues = {
    name: pet?.name,
  };

  const onSubmit = async ({ name }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ name });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toPupGender({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  return (
    <QuizContainer quizStep="pupName">
      <PupNameForm
        index={getStepNumber('pupName')}
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...props}
      />
    </QuizContainer>
  );
};

const PupGender = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack, flags } =
    useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);

  const defaultValues = {
    gender: pet?.gender,
  };
  const genderList = [
    { name: 'Female', value: 'Female' },
    { name: 'Male', value: 'Male' },
  ];
  const onSubmit = async ({ gender }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ gender });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        const nextAction = showPupBreedStep({ flags }, { payload: {} })
          ? ACTIONS.toPupBreed
          : ACTIONS.toPupStatus;
        send(nextAction({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: getStepNumber('pupGender'),
  };

  return (
    <QuizContainer quizStep="pupGender">
      <PupGenderForm
        genderList={genderList}
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupBreed = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack, flags } =
    useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);

  const defaultValues = {
    breeds:
      pet?.breeds?.map(breed => ({
        value: breed.id,
        label: breed.name,
      })) ?? [],
  };

  const onSubmit = async ({ breeds }) => {
    let result;
    let payload;
    const breed_ids = breeds.map(({ value }) => value);

    try {
      const response = await savePet({ breed_ids });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toPupStatus({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const interpolatedProps = interpolate(props, interpolations(pet));
  const cmsProps = {
    ...interpolatedProps,
    index: getStepNumber('pupBreed'),
    labelBreed: interpolatedProps.labelName,
  };

  return (
    <QuizContainer quizStep="pupBreed">
      <PupBreedForm
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupStatus = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack, flags } =
    useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);

  const defaultValues = {
    spayedStatus: pet?.is_neutered?.toString(),
  };

  const onSubmit = async ({ spayedStatus }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ is_neutered: spayedStatus });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toWeight({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: getStepNumber('pupStatus'),
  };

  return (
    <QuizContainer quizStep="pupStatus">
      <PupStatusForm
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Weight = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    flags,
  } = useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const defaultValues = {
    weight: pet?.weight,
    idealWeight: pet?.ideal_weight,
  };

  const onSubmit = async ({ weight, idealWeight }) => {
    let result, payload;
    try {
      const response = await savePet({
        ideal_weight: idealWeight,
        weight,
      });
      payload = {
        action: 'update',
        pet: response.data.pet,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toActivity({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: getStepNumber('weight'),
  };

  cmsProps.weightCallout = getWeightCallout(
    cmsProps.weightCallout,
    pet?.breeds,
  );

  const isPuppy = false;

  return (
    <QuizContainer quizStep="weight">
      <CaloriesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        isPuppy={isPuppy}
      />
    </QuizContainer>
  );
};

const Activity = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    flags,
  } = useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const defaultValues = {
    activity: pet?.activity_level,
  };
  const onSubmit = async ({ activity }) => {
    let result, payload;
    try {
      const response = await savePet({ activity_level: activity });

      payload = {
        action: 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toAllergies({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: getStepNumber('activity'),
  };
  return (
    <QuizContainer quizStep="activity">
      <ActivityForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Allergies = props => {
  const {
    pet,
    cart,
    send,
    goBack,
    goToLogin,
    savePet,
    reporter,
    setShowModal,
    refreshExtras,
    flags,
  } = useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);

  const allAllergies = useAllergies();

  const hasPetAllergies = pet?.allergies && pet?.allergies.length > 0;

  const noAllergiesSelected = `pet:${pet?.id}:no_allergy`;

  const userSelectedNo =
    typeof window !== 'undefined' && localStorage.getItem(noAllergiesSelected);

  const selectedAllergies = useMemo(() => {
    return hasPetAllergies
      ? normalize(getSelectedAllergies(pet?.allergies, allAllergies))
      : [];
  }, [hasPetAllergies, pet?.allergies, allAllergies]);

  const defaultValues = useMemo(
    () => ({
      //* For allergy radio (yes/no)
      allergy: hasPetAllergies
        ? 'true'
        : pet?.starter_box || userSelectedNo
        ? 'false'
        : null,
      //* For allergy selection (multiselect)
      allergies: selectedAllergies,
    }),
    [hasPetAllergies, selectedAllergies, pet?.starter_box, userSelectedNo],
  );

  const onSubmit = async ({ allergy, allergies }) => {
    let result, payload;
    let allergyIds = undefined;

    if (allergy === 'true') {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(noAllergiesSelected);
      }

      allergyIds = allergies?.map(({ value }) => value);
    } else if (allergy === 'false') {
      if (typeof window !== 'undefined') {
        localStorage.setItem(noAllergiesSelected, 'true');
      }
    }

    const currentAllergyNames = (pet?.allergies ?? []).map(a => a.name).sort();
    const submittedAllergyNames =
      allergy === 'true'
        ? allergies
          ? allergies.map(a => a.label).sort()
          : []
        : [];
    const allergiesChanged =
      currentAllergyNames.length !== submittedAllergyNames.length ||
      currentAllergyNames.some((name, i) => name !== submittedAllergyNames[i]);

    try {
      if (allergiesChanged) {
        const response = await savePet(
          {
            allergy_ids:
              allergyIds === undefined || allergyIds.length === 0
                ? undefined
                : allergyIds,
          },
          { waitForMealPlans: true },
        );

        payload = {
          action: 'update',
          pet: response.data.pet,
          pets: response.data.pets,
          allergies: allergies?.map(({ label }) => label) || [],
          allergyIds: allergyIds,
        };
      } else {
        payload = {
          action: 'update',
          pet,
          pets: cart?.pets,
          allergies: allergies?.map(({ label }) => label) || [],
          allergyIds: allergyIds,
        };
      }

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(
          ACTIONS.toRecommendedPlan({
            pet: payload.pet,
            pets: payload.pets,
            cart,
            ...payload,
          }),
        );
        if (allergiesChanged) {
          refreshExtras();
        }
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: getStepNumber('allergies'),
  };

  return (
    <QuizContainer quizStep="allergies">
      <AllergyForm
        defaultValues={defaultValues}
        placeholderAllergiesCount={9}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const RecommendedPlan = props => {
  const {
    setShowModal,
    reporter,
    elevarReporter,
    combinationsByPlanType,
    pet,
    getPetPronoun,
    joinWithOr,
    pet_id: petId,
    cartApi,
    send,
    service,
    pricePeriod,
    goBack,
    setLocalSelection,
    mealPlanForPet,
    flags: { pdpMealPlanTest, hideMixed, showGiftOnboardingStep },
    campaignId,
  } = useContext(OnboardingContext);
  const { type, amount, formattedAmount } = useShopifyOfferContext();
  let combinationId = null;
  const quizCompleteLogged = useRef(false);
  const defaultPlanType = mealPlanForPet
    ? mealPlanForPet.plan_type_name
    : 'fresh';
  const defaultFreshRecipes = mealPlanForPet
    ? mealPlanForPet.recipe_selections.fresh
    : combinationsByPlanType.fresh?.recommendedCombination?.recipes?.fresh ??
      [];
  const defaultBakedRecipes =
    mealPlanForPet?.plan_type_name === 'mixed'
      ? mealPlanForPet.recipe_selections.baked
      : combinationsByPlanType.mixed?.recommendedCombination?.recipes?.baked ??
        [];

  const unavailableFreshRecipes = getUnavailableRecipes(
    combinationsByPlanType.fresh?.availableRecipes?.fresh || [],
  );
  const unavailableBakedRecipes = getUnavailableRecipes(
    combinationsByPlanType.baked?.availableRecipes?.baked || [],
  );

  const unavailableMealPrepTypes = getUnavailableMealPrepTypes(
    combinationsByPlanType,
  );

  const getConfigFromRecipes = (
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  ) => {
    const config = resolveRecipeConfig({
      combinationsByPlanType,
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
      offerType: type,
      offerAmount: amount,
    });

    if (config?.combination_id) {
      combinationId = config.combination_id;
    }

    return config;
  };

  const productTypeInterpolationSubstitutes =
    buildProductTypeInterpolationSubstitutes({
      combinationsByPlanType,
      offerType: type,
      offerAmount: amount,
    });

  const onMealPlanAndRecipesSubmit = async (
    planType,
    freshRecipes,
    bakedRecipes,
  ) => {
    const selectedPlanDefaultCadence =
      combinationsByPlanType[planType]?.cadence;

    if (planType === 'mixed') {
      send(
        ACTIONS.selectedMixedRecipes(
          {
            recipes: {
              dry: bakedRecipes,
              fresh: freshRecipes,
            },
            planType: planType,
            flags: { showGiftOnboardingStep },
          },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
            campaignId,
            defaultCadence: selectedPlanDefaultCadence,
          },
        ),
      );
    } else {
      send(
        ACTIONS.selectedFreshRecipes(
          {
            recipes: freshRecipes,
            planType: planType,
            flags: { showGiftOnboardingStep },
          },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
            campaignId,
            defaultCadence: selectedPlanDefaultCadence,
          },
        ),
      );
    }
    return onCartSubmission(service);
  };

  const mealPlanDescription = `We created a meal plan just for ${capitalize(
    pet.name,
  )}—a ${pet.weight}-pound ${levelToLabel[pet.activity_level]} pup—${
    pet.allergies.length > 0
      ? `and ensured no ${joinWithOr(
          pet.allergies.map(a => a.name.toLowerCase()),
        )} was in ${getPetPronoun(3, 'POSSESSIVE_ADJECTIVE')} recipes `
      : ''
  }so ${getPetPronoun(
    3,
    'SUBJECT',
  )} gets the perfect portions to stay healthy, happy, and thriving.`;

  const mealplanProps = {
    onSubmit: onMealPlanAndRecipesSubmit,
    headline: interpolate(props.headline, interpolations(pet)),
    subheadline: mealPlanDescription,
    petName: pet?.name,
    ctaText: interpolate(props.ctaText, { offer: formattedAmount }),
    modals: [...freshModalsContent, ...dryModalsContent],
    freshRecipes: optimizedRecipes,
    defaultFreshRecipes: defaultFreshRecipes ?? [],
    bakedRecipes: dryRecipes,
    defaultBakedRecipes: defaultBakedRecipes ?? [],
    defaultMealPrepType: defaultPlanType,
    mealPrepTypes: interpolate(
      props.mealPrepTypes,
      productTypeInterpolationSubstitutes,
    ),
    collapsedMealPrepTypes: interpolate(
      props.collapsedMealPrepTypes,
      productTypeInterpolationSubstitutes,
    ),
    unavailableMealPrepTypes: unavailableMealPrepTypes,
    unavailableFreshRecipes: unavailableFreshRecipes,
    unavailableBakedRecipes: unavailableBakedRecipes,
    onBackButtonClick: goBack,
    period: pricePeriod,
    getConfigFromRecipes: getConfigFromRecipes,
    body: props.body,
    headerImage: planHeaderPhoto,
    modalContent: interpolate(
      props.mealPlanModalContent,
      productTypeInterpolationSubstitutes,
    ),
    notSelectableText: `Based on your answers, this meal plan is unavailable for ${pet.name}.`,
    ctaButtonVariant: 'primary',
    hideMixed: hideMixed,
    recipeModalsList: props.recipeModalsList,
  };

  const carouselMealPlanProps = {
    ...mealplanProps,
    subheadline: props.subheadline,
    description: mealPlanDescription,
    freshRecipes: carouselFormRecipes,
  };

  // Only fire quiz complete for new pets, not edits
  useEffect(() => {
    if (quizCompleteLogged.current) return;
    if (!petId || mealPlanForPet) return;
    if (typeof elevarReporter?.elevarCompleteQuiz !== 'function') return;
    try {
      elevarReporter.elevarCompleteQuiz();
      quizCompleteLogged.current = true;
    } catch (error) {
      reporter?.error?.(error, { contexts: { elevar: 'completeQuiz' } });
    }
  }, [elevarReporter, reporter, petId, mealPlanForPet]);

  return pdpMealPlanTest?.includes('pdp_meal_plan') ? (
    <QuizContainer progress={false} centerLogo={true}>
      <CarouselMealPlanForm
        {...carouselMealPlanProps}
        carousel={props.carousel}
      />
    </QuizContainer>
  ) : (
    <QuizContainer progress={false} centerLogo={true}>
      <OptimizedMealPlanForm
        desktopImage={props.desktopImage}
        {...mealplanProps}
      />
    </QuizContainer>
  );
};

RecommendedPlan.displayName = 'RecommendedPlan';
RecommendedPlan.propTypes = {};

const GiftWithPurchase = props => {
  const {
    pet,
    pet_id: petId,
    send,
    goBack,
    reporter,
    setShowModal,
    attachFreeGifts,
    isRouteChanging,
  } = useContext(OnboardingContext);

  const giftType = props.giftTypes?.[0] ?? {};
  const choices = giftType.choices ?? [];

  const addedGiftSkus = pet?.free_gifts?.map(gift => gift.sku) ?? [];
  const selectedVariant =
    choices.find(choice => addedGiftSkus.includes(choice.value)) ?? null;

  const petWeight = pet?.weight;
  const weightBasedChoice =
    petWeight != null
      ? choices.find(
          ({ minWeight, maxWeight }) =>
            minWeight != null &&
            maxWeight != null &&
            petWeight >= minWeight &&
            petWeight <= maxWeight,
        )
      : null;

  const recommendedChoice =
    weightBasedChoice ?? giftType.defaultChoice?.[0] ?? null;

  const onSubmit = async ({ gift }) => {
    if (!gift) {
      return ApiResult.error.client('No gift selected');
    }

    const attachResult = await attachFreeGifts(petId, {
      skus: [gift],
    });

    const result =
      attachResult ?? ApiResult.error.client('Failed to attach gift');

    ApiResult.match(result, {
      success: () => {
        send(ACTIONS.toExtras());
      },
      error: {
        client: error => {
          if (error.original) {
            reporter.error(error.original);
          }
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  const cmsProps = {
    ...interpolate(props, {
      ...interpolations(pet),
      giftName: giftType.name ?? '',
      choiceName: recommendedChoice?.name?.toLowerCase() ?? '',
    }),
  };

  return (
    <QuizContainer progress={false} centerLogo={true}>
      <GiftWithPurchaseForm
        onSubmit={onSubmit}
        goBack={goBack}
        isRouteChanging={isRouteChanging}
        giftType={giftType}
        choices={choices}
        recommendedChoice={recommendedChoice}
        defaultValues={{
          gift: selectedVariant?.value ?? recommendedChoice?.value ?? '',
        }}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Extras = props => {
  const {
    reporter,
    goBack,
    setShowModal,
    pet,
    pet_id: petId,
    attachExtras,
    send,
    pets,
    selectedAddons,
    treatProducts,
    supplementProducts,
    dentalChews,
    treatsForLife,
  } = useContext(OnboardingContext);
  const { formattedAmount, type, amount } = useShopifyOfferContext();
  const treatsAndDentalChews = [...treatProducts, ...dentalChews];
  const allAddonProducts = [...supplementProducts, ...treatsAndDentalChews];
  const { available: defaultAddons, unavailable: unavailableAddons } =
    partitionAddonsByAvailability(
      selectedAddons,
      allAddonProducts,
      BC_TO_SHOPIFY_SKU_MAP,
    );

  const mergedAddonSection = mergeAddonSections(props.addonSections, {
    supplementProducts,
    treatsAndDentalChews,
    skuMap: BC_TO_SHOPIFY_SKU_MAP,
    offerType: type,
    offerAmount: amount,
    formattedAmount,
    petName: pet.name,
  });

  const mergedModals = mergeAddonModals(props.modals, {
    supplementProducts,
    treatsAndDentalChews,
    skuMap: BC_TO_SHOPIFY_SKU_MAP,
    offerType: type,
    offerAmount: amount,
    formattedAmount,
  });

  const onSubmit = createExtrasSubmitHandler({
    attachExtras,
    petId,
    reporter,
    send,
    pet,
    pets,
    skuMap: BC_TO_SHOPIFY_SKU_MAP,
    setShowModal,
  });

  const onSkip = createExtrasSkipHandler({
    submitExtras: onSubmit,
    reporter,
    send,
    pet,
    pets,
    selectedAddons,
    unavailableAddons,
  });

  return (
    <QuizContainer
      overrideText={'Your plan'}
      progress={false}
      centerLogo={true}
    >
      <ExtrasSelectionForm
        headline={interpolate(props.headline, { pupName: pet.name })}
        subheadline={interpolate(props.subheadline, {
          pupName: pet.name,
        })}
        addonSections={mergedAddonSection}
        defaultAddons={defaultAddons}
        unavailableAddons={unavailableAddons}
        onSubmit={onSubmit}
        onSkipButtonClick={onSkip}
        onBackButtonClick={goBack}
        submitButtonText={props.desktopCtaText}
        modals={mergedModals}
        ctaButtonVariant="primary"
        centerSkip={true}
        treatsForLife={treatsForLife}
        pillText={interpolate(props.pillText, { offer: formattedAmount })}
        pillVariant={props.pillVariant}
        body={props.body}
      />
    </QuizContainer>
  );
};

Extras.propTypes = {
  headline: PropTypes.string,
  addonSections: PropTypes.array,
  desktopCtaText: PropTypes.string,
  modals: PropTypes.array,
};

const Checkout = () => {
  const { cart, customer } = useContext(OnboardingContext);
  const checkoutUrl = cart?.checkout_url;
  const customerEmail = customer?.email;

  useEffect(() => {
    let anonymousId = '';
    if (window.analytics && window.analytics.user) {
      anonymousId = window.analytics.user().anonymousId();
    }

    const redirectUrl = buildCheckoutRedirectUrl({
      checkoutUrl,
      locationSearch: window.location?.search,
      anonymousId,
      customerEmail,
    });

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [customerEmail, checkoutUrl]);
  return <LoadingView variant="checkout" />;
};

export const components = {
  [STATE.intro]: IntroPage,
  [STATE.pupName]: PupName,
  [STATE.pupGender]: PupGender,
  [STATE.pupBreed]: PupBreed,
  [STATE.pupStatus]: PupStatus,
  [STATE.weight]: Weight,
  [STATE.activity]: Activity,
  [STATE.allergies]: Allergies,
  [STATE.recommendedPlan]: RecommendedPlan,
  [STATE.giftWithPurchase]: GiftWithPurchase,
  [STATE.quizAbandonerMenu]: AbandonerMenu,
  [STATE.extras]: Extras,
  [STATE.checkout]: Checkout,
};
